// src/theme/chart.ts
//
// Chart colour tokens, derived from the brand ramps in `./brand`.
//
// This file exists so `lib/echarts.ts` stops hand-mirroring `palette.ts`. Those
// two used to be maintained separately and had drifted: a green delta in a
// table was `#34C759` while the same green in a chart was `#10B981`. Anything a
// chart paints now resolves to the same ramp step the rest of the UI uses.
//
// ─── Why the categorical palette is split by mode ────────────────────────────
//
// The old `FINTELA_CHART_COLORS` was a single array serving both themes. That
// cannot be right: a palette optimal on #FFFFFF is not optimal on #07070B, and
// half the old steps failed the perceptual lightness band in one mode or the
// other. Each array below is validated independently against its own surface.
//
// Both pass all six checks of the dataviz validator — lightness band, chroma
// floor, adjacent CVD separation (protan/deutan/tritan), the normal-vision
// floor, and contrast vs surface. `theme/brand.test.ts` re-runs those checks in
// CI, so a future edit that breaks them fails the build rather than shipping.

import { blue, gold, green, neutral, red, seriesDark, seriesLight, type Mode } from './brand';

export type { Mode };

/**
 * Categorical series colours. Order is fixed; hues are never cycled or
 * generated. A 9th series folds into "Other", small multiples, or a composite
 * encoding — it does not get an invented colour.
 */
export const CHART_SERIES: Record<Mode, readonly string[]> = {
  light: seriesLight,
  dark: seriesDark,
};

/** Both arrays are the same length; callers may rely on this. */
export const CHART_SERIES_LENGTH = seriesLight.length;

/**
 * Plum — the accent that marks ROLLING-WINDOW quantities as distinct from the
 * study's fixed stages (Train / Val / OOS / RLP), and the colour a period painted
 * on a chart is drawn in, so the band announces which figures are about to move.
 *
 * A fixed slot in the categorical palette rather than a colour of its own,
 * chosen to sit apart from all five stage colours (slots 0, 1, 4, 5, 7). It lives
 * here, beside the palette it indexes, because `components/` and `lib/` both need
 * it and neither may import from a feature.
 */
const WINDOW_SLOT = 6;

export function windowAccent(mode: Mode): string {
  return CHART_SERIES[mode][WINDOW_SLOT];
}

/**
 * Semantic colours for signed quantities — P&L, drawdown, returns.
 *
 * These are exactly `palette.success.main` / `palette.error.main` for the mode.
 * `brand.test.ts` asserts that equality, because the drift between them is the
 * specific defect this file was created to close.
 *
 * ⚠️ Never use these in a chart that also renders a categorical legend. The
 * semantic green sits near categorical slot 3 (teal) and slot 5 (olive), so the
 * same chart would be asking the reader to distinguish "positive" from
 * "series 4" by hue alone. Semantic colouring is for P&L bars, waterfalls and
 * deltas — surfaces with no categorical dimension.
 */
export interface ChartSemantic {
  positive: string;
  positiveSoft: string;
  negative: string;
  negativeSoft: string;
  /** The middle band of a three-way metric verdict (good / caution / bad). */
  warning: string;
  neutral: string;
}

export const CHART_SEMANTIC: Record<Mode, ChartSemantic> = {
  light: {
    positive: green[600],
    positiveSoft: 'rgba(40, 131, 87, 0.16)',
    negative: red[600],
    negativeSoft: 'rgba(183, 68, 68, 0.16)',
    warning: gold[600],
    neutral: neutral[500],
  },
  dark: {
    positive: green[400],
    positiveSoft: 'rgba(106, 182, 139, 0.18)',
    negative: red[400],
    negativeSoft: 'rgba(235, 126, 122, 0.18)',
    warning: gold[400],
    neutral: neutral[400],
  },
};

/**
 * Sequential — one hue, monotonic in lightness. The brand blue ramp, ordered
 * low → high so the darkest step reads as the largest magnitude in light mode
 * and the brightest does so in dark mode.
 */
export const SEQUENTIAL: Record<Mode, readonly string[]> = {
  light: [blue[50], blue[100], blue[200], blue[300], blue[500], blue[700], blue[900]],
  dark: [blue[900], blue[800], blue[700], blue[600], blue[400], blue[300], blue[100]],
};

/**
 * Diverging for signed metrics — two hues with a NEUTRAL midpoint, never a
 * third hue at the centre. Negative (crimson) ↔ neutral ↔ positive (green),
 * symmetric in lightness about the midpoint so equal magnitudes read equal.
 */
export const DIVERGING: Record<Mode, readonly string[]> = {
  light: [
    red[700], red[600], red[400], red[200],
    neutral[200],
    green[200], green[400], green[600], green[700],
  ],
  dark: [
    red[700], red[600], red[500], red[300],
    neutral[700],
    green[300], green[500], green[600], green[700],
  ],
};

/**
 * Blue → gold, for parameter-performance surfaces. Replaces a genuine rainbow
 * (`#1a237e → #0288d1 → #00897b → #43a047 → #ff8f00` — navy through cyan
 * through teal through green to orange), which destroys magnitude comparison
 * because hue changes carry no ordering. This walks the two brand poles with
 * lightness increasing monotonically, so it can actually be read as a scale.
 */
export const BLUE_GOLD: Record<Mode, readonly string[]> = {
  // The steps are chosen so OKLCH lightness rises monotonically ACROSS the hue
  // change: blue 900→600 (L .335→.545) hands off to gold 500→200 (L .625→.885).
  // Walking each ramp to its own end first and then starting the other would put
  // a lightness peak in the middle, which reads as two scales glued together
  // rather than one — `brand.test.ts` asserts the monotonicity.
  light: [blue[900], blue[800], blue[700], blue[600], gold[500], gold[400], gold[300], gold[200]],
  // Shifted one step brighter: the darkest blue disappears into a dark canvas.
  dark: [blue[800], blue[700], blue[600], blue[500], gold[400], gold[300], gold[200], gold[100]],
};

/** Plotly wants `[stop, color]` pairs over 0…1. */
export const plotlyScale = (stops: readonly string[]): Array<[number, string]> =>
  stops.map((c, i) => [i / (stops.length - 1), c]);

/**
 * Chrome tokens for the chart canvas itself — axes, gridlines, tooltip. These
 * mirror `palette.ts` rather than restating it, so the two cannot drift.
 */
export interface ChartChrome {
  textPrimary: string;
  textSecondary: string;
  textDisabled: string;
  axis: string;
  gridLine: string;
  tooltipBg: string;
  tooltipBorder: string;
  tooltipText: string;
}

export const CHART_CHROME: Record<Mode, ChartChrome> = {
  light: {
    textPrimary: neutral[900],
    textSecondary: neutral[600],
    textDisabled: neutral[400],
    axis: neutral[200],
    gridLine: neutral[150],
    // A tooltip stays dark in both modes — it is an overlay, not a surface.
    // Matches MuiTooltip so a chart tooltip and a UI tooltip are one object.
    tooltipBg: neutral[900],
    tooltipBorder: 'transparent',
    tooltipText: neutral[50],
  },
  dark: {
    textPrimary: neutral[100],
    textSecondary: neutral[400],
    textDisabled: neutral[600],
    axis: neutral[800],
    gridLine: neutral[850],
    tooltipBg: neutral[800],
    tooltipBorder: neutral[700],
    tooltipText: neutral[50],
  },
};
